import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ILogin } from '../../interfaces/login.interface';
import { BehaviorSubject, catchError, map, Observable, of, throwError } from 'rxjs';
import { finalize, shareReplay, take, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ErrorHandlerService } from '../commons/error-handler.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  _httpClient = inject(HttpClient);
  _errorHandler = inject(ErrorHandlerService);
  _router = inject(Router);
  private readonly API_URL = `${environment.apiUrl}/auth`;
  private readonly TOKEN_KEY = 'access_token';
  private readonly SOCKET_TOKEN = 'socket_token';
  private readonly REFRESH_TOKEN = 'refresh_token';

  /** `null` = aún no cargado desde el servidor. */
  private _modulos: string[] | null = null;
  /** `null` = aún no cargado desde el servidor. */
  private _suscripcionActiva: boolean | null = null;
  private cargaModulos$?: Observable<string[]>;
  /** Un solo refresh en vuelo: varios 401 paralelos reutilizan esta llamada. */
  private refreshInFlight$?: Observable<any>;

  private readonly _modulos$ = new BehaviorSubject<string[]>([]);
  private readonly _suscripcionActiva$ = new BehaviorSubject<boolean | null>(null);
  /** Emisión de módulos en memoria; no persiste en localStorage. */
  readonly modulos$ = this._modulos$.asObservable();
  readonly suscripcionActiva$ = this._suscripcionActiva$.asObservable();

  setToken(payload: {
    access_token: string;
    socket_token?: string;
    refresh_token: string;
    modulos?: string[];
    suscripcion_activa?: boolean;
    empresa?: { suscripciones?: { estado: number }[] };
  }): void {
    const { access_token, socket_token, refresh_token, modulos } = payload;
    localStorage.setItem(this.TOKEN_KEY, access_token);
    localStorage.setItem(this.REFRESH_TOKEN, refresh_token);

    if (socket_token) {
      localStorage.setItem(this.SOCKET_TOKEN, socket_token);
    }
    if (Array.isArray(modulos)) {
      this._modulos = [...modulos];
    } else {
      this._modulos = null;
    }
    this._suscripcionActiva = this.obtenerEstadoSuscripcion(payload);
    this.emitirModulos();
    this.cargaModulos$ = undefined;
  }

  private obtenerEstadoSuscripcion(payload: {
    suscripcion_activa?: boolean;
    empresa?: { suscripciones?: { estado: number }[] };
  }): boolean | null {
    if (typeof payload.suscripcion_activa === 'boolean') {
      return payload.suscripcion_activa;
    }
    if (Array.isArray(payload.empresa?.suscripciones)) {
      return payload.empresa.suscripciones.some((suscripcion) => suscripcion.estado === 1);
    }
    return null;
  }

  private emitirModulos(): void {
    this._modulos$.next(this._modulos ?? []);
    this._suscripcionActiva$.next(this._suscripcionActiva);
  }

  private limpiarModulosEnMemoria(): void {
    this._modulos = null;
    this._suscripcionActiva = null;
    this.cargaModulos$ = undefined;
    this._modulos$.next([]);
    this._suscripcionActiva$.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  isAuthenticated() {
    return localStorage.getItem('access_token') !== null ? true : false;
  }

  logout() {
    // Revoca la sesión en el servidor para que el refresh token deje de renovar
    // aunque quede en algún lado. Best-effort: no bloquea la salida.
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN);
    if (refreshToken) {
      this._httpClient
        .post(`${this.API_URL}/logout`, { refreshToken })
        .pipe(catchError(() => of(null)))
        .subscribe();
    }

    this.limpiarModulosEnMemoria();
    // Verificar si viene del backoffice
    const fromBackoffice = localStorage.getItem('from_backoffice');
    localStorage.clear();

    if (fromBackoffice === 'true') {
      let backofficeUrl = window.location.origin;

      if (window.location.pathname.includes('/admin')) {
        backofficeUrl = `${window.location.origin}${window.location.pathname.replace('/admin', '/backoffice')}`;
      } else {
        backofficeUrl = `${window.location.origin}/backoffice`;
      }

      window.location.href = `${backofficeUrl}/dashboard/empresas`;
    } else {
      this._router.navigateByUrl('/auth/login');
    }
  }

  login(body: ILogin): Observable<any> {
    return this._httpClient.post(`${this.API_URL}/login-site`, body).pipe(
      map((response: any) => {
        if (response.payload?.mfa_required) {
          return { mfa_required: true, mfa_token: response.payload.mfa_token };
        }
        this.setToken(response.payload);
        return response.payload;
      }),
      catchError((error: any) => {
        this._errorHandler.received(error);
        throw new Error(error.error.message || 'Error al iniciar sesión');
      })
    );
  }

  /**
   * GET /auth/mis-modulos. Actualiza solo memoria.
   * Prefiere `cargarModulosSiFalta()` en la app (cachea y evita duplicar peticiones).
   */
  private fetchMisModulos(): Observable<string[]> {
    return this._httpClient.get(`${this.API_URL}/mis-modulos`).pipe(
      map((response: any) => ({
        modulos: (response?.payload?.modulos ?? []) as string[],
        suscripcionActiva: response?.payload?.suscripcion_activa as boolean | undefined,
      })),
      tap(({ modulos, suscripcionActiva }) => {
        this._modulos = modulos;
        this._suscripcionActiva = typeof suscripcionActiva === 'boolean' ? suscripcionActiva : true;
        this.emitirModulos();
      }),
      map(({ modulos }) => modulos),
      catchError((error: any) => {
        this._errorHandler.received(error);
        throw new Error(error.error?.message ?? 'Error al obtener módulos');
      })
    );
  }

  cargarEstadoSuscripcionSiFalta(): Observable<boolean> {
    if (this._suscripcionActiva !== null) {
      return of(this._suscripcionActiva);
    }
    return this.cargarModulosSiFalta().pipe(
      map(() => this._suscripcionActiva ?? true)
    );
  }

  suscripcionActiva(): boolean | null {
    return this._suscripcionActiva;
  }

  rutaDashboardInicial(): string {
    return this._suscripcionActiva === false ? '/dashboard/facturacion' : '/dashboard/inicio';
  }

  /**
   * Si no hay módulos en memoria (no cargados) y hay token, pide a la API.
   * Si ya están cargados o no hay token, resuelve sin red.
   */
  cargarModulosSiFalta(): Observable<string[]> {
    if (this._modulos !== null) {
      return of(this._modulos);
    }
    if (!this.getToken()) {
      return of([]);
    }
    if (this.cargaModulos$) {
      return this.cargaModulos$;
    }
    this.cargaModulos$ = this.fetchMisModulos().pipe(
      shareReplay(1),
      finalize(() => {
        this.cargaModulos$ = undefined;
      })
    );
    return this.cargaModulos$;
  }

  /** @deprecated use cargarModulosSiFalta; se mantiene para compat. */
  syncModulos(): Observable<string[]> {
    return this.cargarModulosSiFalta();
  }

  refreshToken(): Observable<any> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN);
    if (!refreshToken) {
      return throwError(() => new Error('No hay refresh token'));
    }
    this.refreshInFlight$ = this._httpClient.post(`${this.API_URL}/refresh`, { refreshToken }).pipe(
      map((response: any) => {
        this.setToken(response.payload);
        return response.payload;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => {
        this.refreshInFlight$ = undefined;
      }),
    );
    return this.refreshInFlight$;
  }

  decodeToken(): any {
    try {
      const token = this.getToken();
      if (!token) {
        return null;
      }
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Lista en memoria. Si aún no se cargó y hay sesión, dispara carga en segundo plano.
   */
  modulos(): string[] {
    if (this._modulos === null && this.getToken()) {
      this.cargarModulosSiFalta()
        .pipe(take(1))
        .subscribe();
    }
    return this._modulos ?? [];
  }

  /** Verifica módulo según el estado en memoria (usar `cargarModulosSiFalta` antes en guards). */
  tieneModulo(modulo: 'RONDAS' | string): boolean {
    return (this._modulos ?? []).includes(modulo);
  }
}
